"use client";

import { useEffect, useRef } from "react";
import Link from "next/link";
import AppImage from "@/components/ui/AppImage";
import Icon from "@/components/ui/AppIcon";

const partnerStats = [
{ value: "34%", label: "Fewer no-shows", icon: "CalendarIcon" },
{ value: "2.4×", label: "More repeat visits", icon: "ArrowPathIcon" },
{ value: "4.9★", label: "Avg. partner rating", icon: "StarIcon" }];


export default function BusinessHero() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.querySelectorAll(".hero-item").forEach((item, i) => {
      (item as HTMLElement).style.animation = `fadeInUp 0.7s cubic-bezier(0.22,1,0.36,1) ${0.1 + i * 0.12}s forwards`;
      (item as HTMLElement).style.opacity = "0";
    });
  }, []);

  return (
    <section
      ref={ref}
      className="relative min-h-[85vh] flex items-center overflow-hidden pt-20"
      style={{ background: "linear-gradient(145deg, #0F2027 0%, #1E3A3A 55%, #0F2027 100%)" }}>
      
      {/* Glow blobs */}
      <div
        className="absolute top-[-15%] right-[-8%] w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(14,165,160,0.14) 0%, transparent 70%)" }} />
      
      <div
        className="absolute bottom-[-10%] left-[-5%] w-[400px] h-[400px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(92,207,203,0.08) 0%, transparent 70%)" }} />
      

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 w-full">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          {/* Left: Copy */}
          <div className="flex flex-col gap-7 z-10">
            <div className="hero-item">
              <span
                className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-full"
                style={{ background: "rgba(14,165,160,0.15)", color: "#5CCFCB" }}>
                
                <Icon name="BuildingOfficeIcon" size={12} />
                For Pet Businesses
              </span>
            </div>
            <h1 className="hero-item text-5xl sm:text-[58px] font-extrabold text-white tracking-tight leading-[1.07]">
              Your clients' pets,
              <br />
              <span className="gradient-text">at your fingertips.</span>
            </h1>
            <p className="hero-item text-lg text-[#8AA4A4] leading-relaxed max-w-[500px]">
              DogFob gives your practice or service a live view of every client's pet profile — before they walk in the door. No paper forms. No missed follow-ups. No lost clients.
            </p>

            {/* Stat pills */}
            <div className="hero-item flex flex-wrap gap-3">
              {partnerStats.map((stat) =>
              <div
                key={stat.label}
                className="glass-card-dark rounded-2xl px-4 py-3 flex items-center gap-3 border border-white/10">
                
                  <Icon name={stat.icon as "CalendarIcon"} size={16} className="text-teal-400" />
                  <div>
                    <p className="text-lg font-extrabold text-white font-heading leading-none">{stat.value}</p>
                    <p className="text-[10px] text-[#8AA4A4]">{stat.label}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="hero-item flex flex-col sm:flex-row gap-3">
              <Link href="#apply" className="btn-primary text-base py-3.5 px-7 transition-all duration-200 hover:scale-105 hover:shadow-lg">
                <Icon name="ClipboardDocumentCheckIcon" size={18} />
                Apply as Partner
              </Link>
              <Link href="#how-it-works" className="btn-ghost-white text-base py-3.5 px-7 transition-all duration-200 hover:scale-105 hover:shadow-lg">
                See How It Works
              </Link>
            </div>
          </div>

          {/* Right: Image */}
          <div className="relative">
            <div
              className="hero-item rounded-3xl overflow-hidden"
              style={{
                boxShadow: "0 32px 80px rgba(0,0,0,0.4)",
                opacity: 0,
                animation: "scaleIn 0.8s cubic-bezier(0.22,1,0.36,1) 0.4s forwards"
              }}>
              
              <AppImage
                src="https://img.rocket.new/generatedImages/rocket_gen_img_1a2c0f7ba-1767451838497.png"
                alt="Professional female veterinarian in a modern clinic examining a small white dogfob on the table"
                width={600}
                height={460}
                className="w-full h-[440px] object-cover"
                priority />
              
              <div className="absolute inset-0 bg-gradient-to-t from-[#0F2027]/50 to-transparent" />
            </div>

            {/* Partner badge */}
            <div
              className="absolute -bottom-5 -left-5 glass-card-dark rounded-2xl p-4 animate-float-a border border-white/10"
              style={{ boxShadow: "0 16px 48px rgba(0,0,0,0.35)" }}>
              
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-teal-500/20 flex items-center justify-center">
                  <Icon name="CheckBadgeIcon" size={20} className="text-teal-400" variant="solid" />
                </div>
                <div>
                  <p className="text-xs text-[#8AA4A4]">Verified partner</p>
                  <p className="text-sm font-bold text-white font-heading">Riverside Animal Clinic</p>
                  <p className="text-[10px] text-teal-400">Joined DogFob · Jan 2026</p>
                </div>
              </div>
            </div>

            {/* Notification */}
            <div
              className="absolute top-5 -right-3 sm:-right-6 glass-card-dark rounded-xl px-4 py-3 animate-float-b border border-white/10 max-w-[200px]"
              style={{ boxShadow: "0 12px 36px rgba(0,0,0,0.3)" }}>
              
              <div className="flex items-center gap-2 mb-1">
                <Icon name="BellIcon" size={12} className="text-teal-400" />
                <span className="text-[10px] text-teal-400 font-bold">New client</span>
              </div>
              <p className="text-xs font-semibold text-white font-heading">Max (Labrador) booked</p>
              <p className="text-[10px] text-[#8AA4A4]">Annual checkup · Tomorrow 9am</p>
            </div>
          </div>
        </div>
      </div>
    </section>);

}